import { createTheme } from "@mui/material/styles";

export const checkCircleColor = "hsl(var(--color-secondary))";
export const linkColor = "hsl(var(--color-primary))";
export const ingredientsBackgroundColor = "hsl(var(--color-primary-50))";
export const BORDER_COLOR = "hsl(var(--color-primary-100))";
export const BLUE_BG = "hsl(var(--color-primary-200))";
export const LIGHT_BLUE_BG = "hsl(var(--color-primary-50))";
export const LIGHT_GREEN_BG = "hsl(var(--color-secondary-50))";
export const BLACK_40 = "hsl(var(--color-gray-700))";

export const BOX_SHADOW = "0px 5px 4px hsl(var(--color-gray-900) / 0.25)";

const fontFamily = [
  "Silka",
  "HelveticaNeue",
  "Helvetica Neue",
  "sans-serif",
].join(",");

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: false;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    "2xl": true; // adds the `2xl` breakpoint
  }
}

const baseTheme = createTheme({
  typography: {
    fontFamily,
    fontWeightMedium: 600,
  },
  breakpoints: {
    values: {
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
      "2xl": 1536,
    },
  },
  palette: {
    primary: {
      contrastText: "hsl(var(--color-white))",
      main: "hsl(var(--color-primary))",
      light: "hsl(var(--color-primary-400))",
    },
    secondary: {
      main: "hsl(var(--color-secondary))",
    },
    warning: {
      main: "hsl(var(--color-yellow))",
    },
    text: {
      primary: "hsl(var(--color-gray-700))",
      secondary: "hsl(var(--color-gray-700))",
      disabled: "hsl(var(--color-gray)",
    },
  },
});

const { palette, breakpoints } = baseTheme;

const withTypography = createTheme(baseTheme, {
  typography: {
    ...baseTheme.typography,
    h3: {
      fontWeight: baseTheme.typography.fontWeightMedium,
      color: palette.primary.main,
      fontSize: "32px",
      lineHeight: "40px",
      [breakpoints.down("sm")]: {
        fontSize: "24px",
        lineHeight: "28px",
      },
    },
    h4: {
      fontSize: "24px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "30px",
      letterSpacing: "0",
    },
    h5: {
      fontSize: "20px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "24px",
      letterSpacing: "0",
    },
    h6: {
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "18px",
      letterSpacing: "0em",
    },
    button: {
      // styleName: Btn1;
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 600,
      lineHeight: "16px",
      letterSpacing: "0.1em",
    },
    caption: {
      color: palette.text.primary,
      fontSize: "12px",
      lineHeight: "16px",
    },
    body1: {
      color: palette.text.primary,
      fontSize: "16px",
      lineHeight: "130%",
      fontStyle: "normal",
      letterSpacing: "0em",
      fontWeight: 400,
    },
    body2: {
      color: palette.text.primary,
      fontSize: "14px",
      fontWeight: 400,
      fontStyle: "normal",
      lineHeight: "130%",
      letterSpacing: "0em",
    },
  },
});

export const theme = createTheme(withTypography, {
  components: {
    MuiStepper: {
      root: {
        padding: "0",
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "always",
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        focusVisibleClassName: undefined,
      },
      switchBase: {
        padding: 0,
        color: "hsl(var(--color-gray-200))",
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "hsl(var(--color-white))",
          "& + .track": {
            backgroundColor: palette.primary.main,
            opacity: 1,
            border: "none",
          },
        },
      },
      track: {
        borderRadius: 10,
        height: undefined,
        border: `1px solid hsl(var(--color-gray-400))`,
        backgroundColor: "hsl(var(--color-gray-200))",
        opacity: 1,
        transition: baseTheme.transitions.create([
          "background-color",
          "border",
        ]),
      },
      styleOverrides: {
        root: {
          width: 36,
          height: 20,
          padding: 0,
          overflow: undefined,
        },
        thumb: {
          width: 20,
          height: 20,
          backgroundColor: "hsl(var(--color-white))",
          boxShadow: `0 0 0 1px currentColor inset`,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "hsl(var(--color-gray-900) / 0.6)",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          "&:hover": {
            backgroundColor: undefined,
          },
        },
        sizeLarge: {
          padding: "7px",
          fontSize: "unset",
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        groupedOutlinedHorizontal: {
          "&:not(:last-child)": {
            borderRightColor: undefined,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        light: {
          backgroundColor: LIGHT_BLUE_BG,
        },
        root: {
          backgroundColor: BLUE_BG,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
        disableTouchRipple: true,
      },
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: undefined,
            "@media (hover: none)": {
              backgroundColor: undefined,
            },
          },
        },
      },
    },
  },
});

export default theme;
